<script setup lang="ts">
  import { toTypedSchema } from '@vee-validate/zod';
  import { useForm } from 'vee-validate';
  import { ref, watch } from 'vue';
  import * as zod from 'zod';

  import type { Note, Partner } from '@stuut/api/models';
  import { Button } from '@ui/components/button';
  import { ComboSelect } from '@ui/components/combo-select';
  import { useLoader } from '@ui/components/combo-select/useLoader';
  import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
  } from '@ui/components/dialog';
  import {
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
  } from '@ui/components/form';
  import { toast } from '@ui/components/sonner';
  import { Textarea } from '@ui/components/textarea';

  import { stuut } from '@/api';
  import NoteConfirmationDialog from '@/components/notes/NoteConfirmationDialog.vue';
  import { MAX_NOTE_LENGTH } from '@/constants/CustomerNotes';
  import { useNotes } from '@/store';

  interface Props {
    createNoteForPartnerProps?: {
      partnerId: string;
    };
    editNoteProps?: {
      note: Note;
      partnerId: string;
    };
    isOpen: boolean;
  }

  const props = defineProps<Props>();
  const emit = defineEmits(['update:isOpen']);
  const notesStore = useNotes();

  const selectedPartner = ref<Partner>();
  watch(selectedPartner, (newPartner) => {
    form.setFieldValue('partner', newPartner?.id ?? undefined);
  });

  const partnerLoader = useLoader({
    load: (args?) => stuut.partners.list({ params: args || {} }),
  });

  const confirmationDialogOpen = ref(false);

  const form = useForm({
    validationSchema: toTypedSchema(
      zod.object({
        content: zod
          .string({
            required_error: 'Your note cannot be blank.',
          })
          .min(1, {
            message: 'Note must be at least 1 character.',
          })
          .max(MAX_NOTE_LENGTH, {
            message: `Note must not be longer than ${MAX_NOTE_LENGTH} characters.`,
          }),
        partner: zod.string({
          required_error: 'You must select a customer.',
        }),
      }),
    ),
  });

  const initFormState = async () => {
    const partnerId = props.editNoteProps?.partnerId || props.createNoteForPartnerProps?.partnerId;

    form.resetForm({
      values: {
        content: props.editNoteProps?.note.content,
        partner: partnerId,
      },
    });
  };

  watch(
    () => props.isOpen,
    async (isOpen) => {
      if (isOpen) {
        await initFormState();
      } else {
        form.resetForm();
      }
    },
  );

  const handleOpen = (open: boolean) => {
    if (!open && form.meta.value.dirty) {
      confirmationDialogOpen.value = true;
    } else {
      emit('update:isOpen', open);
    }
  };

  const deleteDialogOpen = ref(false);

  const openDeleteConfirmDialog = (event: Event) => {
    event.stopPropagation(); // Prevent note editor from coming up when clicking delete button
    deleteDialogOpen.value = true;
  };
  const deleteNote = async () => {
    if (!props.editNoteProps?.note) return;

    try {
      await notesStore.deleteNote(props.editNoteProps.note);
    } catch (error: any) {
      toast.error(error.clientMessage);
    }
  };

  const isSubmitting = ref(false);
  const onSubmit = (e: Event) => {
    e.preventDefault();
    if (isSubmitting.value) {
      return;
    }

    isSubmitting.value = true;
    form
      .validate()
      .then(async (result) => {
        if (!result.valid) {
          return;
        }

        if (props.editNoteProps) {
          notesStore.updateNote(props.editNoteProps.note, {
            content: result.values?.content,
          });
        } else {
          notesStore.createNote({
            content: result.values?.content ?? '',
            partner: result.values?.partner ?? '',
          });
        }
        emit('update:isOpen', false);
      })
      .catch((err) => {
        toast.error(err.clientMessage);
      })
      .finally(() => {
        isSubmitting.value = false;
      });
  };
</script>

<template>
  <Dialog :open="isOpen" :modal="true" @update:open="(value) => handleOpen(value)">
    <DialogContent class="px-6 max-w-xl border-none">
      <DialogHeader class="hidden">
        <DialogTitle class="text-muted-foreground">
          {{ editNoteProps !== undefined ? 'Edit Note' : 'Add New Note' }}
        </DialogTitle>
      </DialogHeader>

      <form class="flex flex-col space-y-4" @submit="onSubmit">
        <FormField name="partner" :validate-on-blur="false" :validate-on-model-update="false">
          <FormItem v-if="editNoteProps === undefined && createNoteForPartnerProps === undefined">
            <FormLabel class="text-foreground text-xs" asterisk>Customer</FormLabel>
            <ComboSelect
              v-model="selectedPartner"
              :loader="partnerLoader"
              searchable
              variant="dropdown"
              :placeholder="selectedPartner?.name ?? 'Search Customer'"
              search-placeholder="Search customer..."
              section-heading="Customers"
              option-identifier="name"
            />
            <FormMessage v-if="form.errors.value.partner" class="text-destructive">{{
              form.errors.value.partner
            }}</FormMessage>
          </FormItem>
        </FormField>

        <FormField
          v-slot="{ componentField }"
          name="content"
          :validate-on-blur="false"
          :validate-on-model-update="false"
        >
          <FormItem>
            <FormControl>
              <Textarea
                v-bind="componentField"
                class="resize-none focus-visible:ring-white focus-visible:ring-offset-0 border border-border max-h-[50vh] overflow-y-auto"
                placeholder="Write your note here..."
                rows="14"
                :maxlength="MAX_NOTE_LENGTH"
              />
            </FormControl>
            <FormDescription class="text-muted-foreground"
              >Max {{ MAX_NOTE_LENGTH }} characters</FormDescription
            >
            <FormMessage v-if="form.errors.value.content" class="text-destructive">{{
              form.errors.value.content
            }}</FormMessage>
          </FormItem>
        </FormField>

        <DialogFooter>
          <Button type="button" variant="secondary" @click="handleOpen(false)"> Close </Button>
          <Button type="submit" variant="primary">Save</Button>

          <Button
            v-if="editNoteProps !== undefined"
            variant="ghost"
            size="sm"
            class="text-destructive"
            @click="openDeleteConfirmDialog"
          >
            <i class="fa-regular fa-trash-can"></i>
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
  <NoteConfirmationDialog
    v-model:is-open="confirmationDialogOpen"
    title="Are you sure?"
    description="You have unsaved changes to your content"
    @confirmed="emit('update:isOpen', false)"
  />

  <NoteConfirmationDialog
    v-model:is-open="deleteDialogOpen"
    title="Delete note?"
    description="Are you sure you want to delete this note?"
    @confirmed="deleteNote"
  />
</template>

<style scoped></style>
